import { APP_CATEGORY_LABELS, type Action, type Decision } from '../types.js';
import type { PolicyRule } from './schema.js';

const ACTION_LABELS: Record<Action, string> = {
  navigate: 'opens',
  paste: 'pastes',
  upload: 'uploads a file',
  download: 'downloads a file',
  submit: 'submits a prompt or form',
};

export const ACTION_NOUNS: Record<Action, string> = {
  navigate: 'visit',
  paste: 'paste',
  upload: 'upload',
  download: 'download',
  submit: 'submission',
};

export const DECISION_LABELS: Record<Decision, string> = {
  allow: 'Allow',
  log: 'Allow and log',
  warn: 'Warn',
  justify: 'Require justification',
  block: 'Block',
};

export interface NameLookup {
  user?(id: string): string | undefined;
  group?(id: string): string | undefined;
  app?(id: string): string | undefined;
  classification?(id: string): string | undefined;
}

function list(values: readonly string[], name: (v: string) => string | undefined, conj = 'or'): string {
  const names = values.map((v) => name(v) ?? 'unknown');
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} ${conj} ${names[names.length - 1]}`;
}

/** Plain-English sentence describing a rule, e.g. for policy lists and audit views. */
export function explainRule(rule: Pick<PolicyRule, 'conditions' | 'action'>, n: NameLookup = {}): string {
  const c = rule.conditions;
  const parts: string[] = ['When'];
  const who: string[] = [];
  if (c.users) who.push(`${c.users.op === 'not_in' ? 'anyone except ' : ''}${list(c.users.values, (v) => n.user?.(v))}`);
  if (c.groups) who.push(`${c.groups.op === 'not_in' ? 'anyone outside ' : 'members of '}${list(c.groups.values, (v) => n.group?.(v))}`);
  parts.push(who.length ? who.join(' and ') : 'anyone');
  if (c.deviceManaged) parts.push(`on ${c.deviceManaged} devices`);
  if (c.browsers) parts.push(`in ${list(c.browsers, (b) => (b === 'edge' ? 'Edge' : b === 'chrome' ? 'Chrome' : 'other browsers'))}`);
  if (c.accountTypes) parts.push(`using a ${list(c.accountTypes, (t) => t)} account`);
  const dest: string[] = [];
  if (c.apps) dest.push(`${c.apps.op === 'not_in' ? 'any app except ' : ''}${list(c.apps.values, (v) => n.app?.(v))}`);
  if (c.appCategories)
    dest.push(
      `${c.appCategories.op === 'not_in' ? 'any app outside ' : ''}${list(c.appCategories.values, (v) => APP_CATEGORY_LABELS[v as keyof typeof APP_CATEGORY_LABELS])}`,
    );
  if (c.appApproval) dest.push(`(${list(c.appApproval, (a) => a)} apps)`);
  parts.push(c.actions ? list(c.actions, (a) => ACTION_LABELS[a as Action]) : 'uses');
  parts.push(dest.length ? `in ${dest.join(' ')}` : 'any application');
  if (c.data) {
    const d: string[] = [];
    if (c.data.classifications?.length) d.push(list(c.data.classifications, (v) => n.classification?.(v)));
    if (c.data.minSensitivity) d.push(`${c.data.minSensitivity} or more sensitive data`);
    if (c.data.orUninspectable) d.push('content that could not be inspected');
    parts.push(`and the data contains ${d.join(' or ')}`);
  }
  if (c.file?.extensions?.length) parts.push(`(.${c.file.extensions.join(', .')} files)`);
  return `${parts.join(' ')} → ${DECISION_LABELS[rule.action]}`;
}
